import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";

import { BaseForm, FormInput, FormToggle } from "components/BaseForm";
import { Spinner } from "@/components/ui/spinner";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import AllowedTermsField from "routes/product/pricing/AllowedTermsField";
import PriceBasisField from "routes/product/pricing/PriceBasisField";
import PriceVersionsField from "routes/product/pricing/PriceVersionsField";
import { PRICE_BASIS } from "routes/product/pricing/pricing";
import ProductService from "services/Product";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  list_price: z
    .string()
    .min(1, "List Price is required")
    .regex(
      /^\d+(\.\d{1,2})?$/,
      "List Price must be a number with at most 2 decimal places"
    ),
  enabled: z.boolean(),
});

const pricingSchema = z.object({
  name: z.string().min(1, "Name is required"),
  enabled: z.boolean(),
  price_basis: z.enum([PRICE_BASIS.PER_YEAR, PRICE_BASIS.WHOLE_TERM]),
  allowed_terms: z
    .array(z.number().int().min(1))
    .min(1, "At least one allowed term is required."),
  prices: z
    .array(
      z.object({
        id: z.number().optional(),
        amount: z
          .string()
          .regex(
            /^\d+(\.\d{1,2})?$/,
            "Enter a price greater than $0 with at most 2 decimal places."
          )
          .refine(
            amount => parseFloat(amount) > 0,
            "Enter a price greater than $0 with at most 2 decimal places."
          ),
        is_current: z.boolean(),
        enabled: z.boolean(),
        is_used: z.boolean(),
      })
    )
    .min(1, "At least one price is required."),
});

const toPricingValues = product => ({
  name: product?.name ?? "",
  enabled: product?.enabled ?? true,
  price_basis: product?.price_basis ?? PRICE_BASIS.PER_YEAR,
  allowed_terms: product?.allowed_terms ?? [],
  prices: (product?.prices ?? []).map(price => ({
    id: price.id,
    amount: String(price.amount),
    is_current: price.is_current,
    enabled: price.enabled,
    is_used: price.is_used,
  })),
});

const toPricingPayload = values => ({
  name: values.name,
  enabled: values.enabled,
  price_basis: values.price_basis,
  allowed_terms: values.allowed_terms,
  prices: values.prices.map(({ id, amount, is_current, enabled }) => ({
    ...(id != null && { id }),
    amount,
    is_current,
    enabled,
  })),
});

function ProductForm({ model, onSuccess }) {
  const { enable_product_pricing } = useRuvixxFlags();
  const { data: product } = useQuery({
    queryKey: ["product", model?.id],
    queryFn: () => ProductService.get(model.id),
    enabled: !!model?.id,
  });

  const initialValues = useMemo(() => {
    if (model?.id && !product) return null;
    if (enable_product_pricing) return toPricingValues(product);
    if (!model?.id) {
      return { name: "", list_price: "0", enabled: true };
    }
    return {
      name: product.name,
      list_price: String(product.list_price ?? ""),
      enabled: product.enabled,
    };
  }, [model?.id, product, enable_product_pricing]);

  const handleSubmit = async values => {
    const data = enable_product_pricing
      ? toPricingPayload(values)
      : {
          name: values.name,
          list_price: parseFloat(values.list_price),
          enabled: values.enabled,
        };
    if (model?.id) {
      await ProductService.update(model.id, data);
    } else {
      await ProductService.create(data);
    }
    toast.success("Product saved");
  };

  if (!initialValues) {
    return <Spinner />;
  }

  return (
    <BaseForm
      schema={enable_product_pricing ? pricingSchema : schema}
      defaultValues={initialValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
    >
      <FormInput name="name" label="Name" required />
      {enable_product_pricing ? (
        <>
          <PriceVersionsField />
          <AllowedTermsField />
          <PriceBasisField isUsed={Boolean(product?.is_used)} />
        </>
      ) : (
        <FormInput
          name="list_price"
          label="List Price"
          type="number"
          required
        />
      )}
      <FormToggle name="enabled" label="Enabled" />
    </BaseForm>
  );
}

export default ProductForm;
